import React from 'react';
import i18n from '../../../i18n';
import { Field, reduxForm } from 'redux-form';
import { string } from 'yup';
import { makeStyles } from '@material-ui/core';
import { isPossiblePhoneNumber } from 'react-phone-number-input';

import { useFormError, validator } from 'core/form/form';

import { MobilePhoneControlField } from 'components/MobilePhoneControl/MobilePhoneControl';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { Text, TextTypography } from 'components/Text/Text';
import { TextControlField } from 'components/TextControl/TextControl';
import { TermsAndConditions } from 'components/TermsAndConditions/TermsAndConditions';

import { Form } from 'forms/types';

import { PromoVideo } from './PromoVideo';
import { DefaultFooter } from './DefaultFooter';

import { SellerFormData } from './types';
import queryString from 'query-string';
import { useActionDispatchOnMount } from 'core/store/store';
import { makeSignupSaveMetricsAction } from 'state/signup/actions';
import ReCAPTCHA from 'react-google-recaptcha';

const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1.25, 1.25, 0, 0),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        [theme.breakpoints.down('md')]: {
            borderRadius: 0,
        },
    },
    main: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(3),
    },
    header: {
        borderBottom: `1px solid ${theme.palette.brandPrimary.lightest}`,
        paddingBottom: theme.spacing(3),
    },
    title: {
        marginBottom: theme.spacing(1),
    },
    panes: {
        display: 'flex',
        flex: 1,
        padding: theme.spacing(3, 0),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
    leftPane: {
        flex: 1,
    },
    rightPane: {
        flex: 1,
        paddingLeft: theme.spacing(9),
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
    },
    field: {
        marginBottom: theme.spacing(3),
    },
    footer: {
        flexBasis: 'auto',
    },
}));
const recaptchaKey = '6LdwmqcnAAAAACt0mbMAqqBjLRX9T3EmmqhWjTYd';

const Captcha = (props: any) => (
    <div>
        <ReCAPTCHA
            sitekey={process.env.RECAPTCHA_SITE_KEY || recaptchaKey}
            onChange={props.input.onChange}
        />
    </div>
);

export const Seller = reduxForm<SellerFormData>({
    form: Form.ONBOARDING_SELLER_FORM,
    validate: validator({
        captcha: string().required('common:validation.requiredCaptcha'),
        email: string().email('common:validation.emailFormat').required('common:validation.required'),
        phone: string()
            .required('common:validation.required')
            .test({
                message: 'common:validation.phoneFormat',
                test: value => {
                    if (!value) {
                        return true;
                    }
                    return isPossiblePhoneNumber(value);
                },
            }),
    }),
})(props => {
    const { handleSubmit } = props;
    const { t } = i18n.useTranslation();
    const error = useFormError(Form.ONBOARDING_SELLER_FORM);

    const search = queryString.parse(window.location.search);
    useActionDispatchOnMount(true, makeSignupSaveMetricsAction, search?.correlationId, search?.utm_source);

    const classes = useStyles();

    return (
        <ErrorBoundary>
            <form className={classes.container} onSubmit={handleSubmit}>
                <main className={classes.main}>
                    <header className={classes.header}>
                        <Text className={classes.title} typography={TextTypography.TITLE}>
                            {t('onboarding:seller.title')}
                        </Text>
                        <Text typography={TextTypography.BODY}>{t('onboarding:seller.description')}</Text>
                    </header>
                    <div className={classes.panes}>
                        <div className={classes.leftPane}>
                            <div className={classes.field}>
                                <Field
                                    name="phone"
                                    component={MobilePhoneControlField}
                                    label={t('onboarding:seller.phone')}
                                />
                                <p>{t('onboarding:seller.phoneDescription')}</p>
                            </div>
                            <div className={classes.field}>
                                <Field name="name" component={TextControlField} label={t('onboarding:seller.name')} />
                            </div>
                            <div className={classes.field}>
                                <Field name="email" component={TextControlField} label={t('onboarding:seller.email')} />
                            </div>
                            <div className={classes.field}>
                                <Field name='captcha' component={Captcha}/>
                            </div>
                            {error && <FormErrorMessage error={error} />}
                        </div>
                        <div className={classes.rightPane}>
                            <PromoVideo />
                        </div>
                    </div>
                    <div>
                        <TermsAndConditions buttonText={t('onboarding:footer.nextButton')} />
                    </div>
                </main>
                <footer className={classes.footer}>
                    <DefaultFooter />
                </footer>
            </form>
        </ErrorBoundary>
    );
});
